import { useState } from 'react';
import axios from 'axios';
import { Mail, MessageSquare, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-28 bg-black min-h-screen" data-testid="contact-success">
        <div className="max-w-2xl mx-auto px-6 md:px-12 py-24 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Message Received!
          </h1>
          <p className="text-lg text-[#999999] mb-8">
            Thank you for reaching out. We'll get back to you within 24-48 hours.
          </p>
          <Button 
            onClick={() => setSubmitted(false)}
            variant="outline"
            className="btn-hover border-[#1A1A1A] text-white hover:bg-[#1A1A1A] hover:text-white rounded-none px-8 py-4 text-sm uppercase tracking-widest"
          >
            Send Another Message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 bg-black min-h-screen" data-testid="contact-page">
      {/* Header */}
      <section className="py-16 md:py-24 border-b border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Get In Touch</p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-6">
            Contact Us
          </h1>
          <p className="text-lg text-[#999999] max-w-2xl">
            Have a question or need support? We're here to help. Fill out the form below and we'll get back to you as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16">
            {/* Form */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Your Name
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="rounded-none border-[#1a1a1a] focus:border-[#1A1A1A] py-6"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="rounded-none border-[#1a1a1a] focus:border-[#1A1A1A] py-6"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Message
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="How can we help you?"
                    rows={6}
                    className="rounded-none border-[#1a1a1a] focus:border-[#1A1A1A] resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full btn-hover bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-none py-6 text-sm uppercase tracking-widest font-semibold"
                  data-testid="contact-submit-button"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <span className="spinner mr-2" />
                      Sending...
                    </span>
                  ) : (
                    <>
                      Send Message
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="lg:pl-12">
              <div className="space-y-8">
                <div>
                  <h2 className="font-serif text-2xl text-white mb-6">
                    Other Ways to Reach Us
                  </h2>
                  <p className="text-[#999999] leading-relaxed">
                    We typically respond to all inquiries within 24-48 hours during business days. 
                    For urgent matters, please indicate so in your message.
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#0f0f0f] flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Email</h3>
                      <p className="text-[#999999]">support@theraresmethod.com</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#0f0f0f] flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Response Time</h3>
                      <p className="text-[#999999]">24-48 hours on business days</p>
                    </div>
                  </div>
                </div>

                {/* FAQ Link */}
                <div className="bg-[#0f0f0f] border border-[#1a1a1a] p-6">
                  <h3 className="font-serif text-xl text-white mb-3">
                    Check Our FAQ
                  </h3>
                  <p className="text-sm text-[#999999] mb-4">
                    Many common questions are already answered in our FAQ section.
                  </p>
                  <a 
                    href="/faq" 
                    className="text-[#D4AF37] text-sm uppercase tracking-widest font-semibold hover:underline inline-flex items-center"
                  >
                    View FAQ
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
